import { useRoute, useRouter, type RouteLocationRaw } from 'vue-router'
import { useGlobalSearch } from './model.ts'
import type { FilterCondition, GlobalSearchType } from './type.ts'

/**
 * Удаляет локальные служебные ключи фильтров перед отправкой в API или сохранением пресета.
 */
export const stripFilterLocalKeys = (filter: FilterCondition[][]): FilterCondition[][] =>
  filter.map((group) => group.map(({ _key, ...condition }) => condition))

/**
 * Создаёт стабильный локальный ключ для корректного обновления элементов фильтра в v-for.
 */
export const createFilterKey = () => crypto.randomUUID()

/**
 * Возвращает true, если результат поиска — элемент инвентаря (объект на карте),
 * а не строка справочника типов ('inv_type').
 *
 * Search-сервис заполняет `type` только когда запрос содержит текст (`q`): в основном
 * сценарии расширенного поиска (по типу объекта и полям, без текста) `type` у всех
 * записей приходит пустым. Поэтому вместо `item.type === 'elem'` проверяем "не
 * inv_type" — так поведение карты/перехода не зависит от того, заполнено ли поле.
 */
export const isSearchResultElement = (type: string): boolean => type !== 'inv_type'

/**
 * Composable для навигации по результатам глобального поиска.
 * Инкапсулирует логику переходов на паспорт объекта, карту или тип инвентаря.
 * Переходы открываются в новой вкладке, чтобы не сбрасывать состояние поиска.
 */
export const useGlobalSearchNavigation = () => {
  const router = useRouter()
  const route = useRoute()
  const model = useGlobalSearch()

  /** Базовые params группы/шкафа без текущего tab (на /search он равен search). */
  const baseObjectParams = () => ({
    type: String(route.params.type),
    object_id: String(route.params.object_id),
  })

  /**
   * Открывает маршрут во вкладке. Нельзя спредить route.params целиком:
   * со страницы поиска tab=search, а паспорт требует tab=inventory —
   * иначе URL вида /objects/.../search/elements/... даёт 404.
   */
  const openInNewTab = (location: RouteLocationRaw) => {
    const resolved = router.resolve(location)
    window.open(resolved.fullPath, '_blank', 'noopener,noreferrer')
  }

  /**
   * Открывает объект из результатов поиска в новой вкладке.
   *
   * @param item    - элемент результата поиска
   * @param isMap   - если true и результат не 'inv_type', открывает карту с выделенным объектом
   */
  const navigateTo = (item: GlobalSearchType, isMap: boolean = false) => {
    if (isMap && isSearchResultElement(item.type)) {
      openInNewTab({
        name: `objects.${String(route.params.type)}.map`,
        params: { ...baseObjectParams(), tab: 'map' },
        query: { ids: String(item.id) },
      })
    } else if (item.type === 'inv_type') {
      openInNewTab({
        name: 'objects.groups.admin',
        params: {
          ...baseObjectParams(),
          tab: 'admin',
          section: 'passports-config',
          action: 'edit-v2',
          entity_id: item.id,
        },
      })
    } else {
      openInNewTab({
        name: 'object.inventory.elements.passport-v2',
        params: {
          ...baseObjectParams(),
          tab: 'inventory',
          tab_inventory: 'elements',
          invent_object_id: String(item.id),
        },
      })
    }

    model.open = false
  }

  /**
   * Открывает карту со всеми найденными элементами в новой вкладке.
   *
   * @param items - элементы результата поиска
   */
  const navigateToMap = (items: GlobalSearchType[]) => {
    const ids: number[] = []

    items.forEach((item) => {
      if (isSearchResultElement(item.type)) ids.push(item.id)
    })

    openInNewTab({
      name: `objects.${String(route.params.type)}.map`,
      params: { ...baseObjectParams(), tab: 'map' },
      query: { ids: ids.join(',') },
    })

    model.open = false
  }

  return { navigateTo, navigateToMap }
}
